import { useEffect, useRef, useState } from "react";

export function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms);
  const total = Math.floor(clamped / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${h}:${pad(m)}:${pad(s)}`;
}

/**
 * Live countdown to a real-time deadline. Ticks once a second; fires onExpire
 * exactly once per deadlineMs when that deadline passes while mounted. A new
 * deadlineMs (e.g. after a reroll) resets the guard so onExpire can fire again.
 */
export function useCountdown(
  deadlineMs: number,
  onExpire?: () => void,
): { remainingMs: number; label: string; expired: boolean } {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remainingMs = deadlineMs - now;
  const expired = remainingMs <= 0;

  const firedForDeadline = useRef<number | null>(null);

  useEffect(() => {
    if (expired && onExpire && firedForDeadline.current !== deadlineMs) {
      firedForDeadline.current = deadlineMs;
      onExpire();
    }
  }, [expired, onExpire, deadlineMs]);

  return { remainingMs, label: formatRemaining(remainingMs), expired };
}
